import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

@Component({
  standalone: true,
  imports: [CommonModule],
  selector: 'app-image-viewer',
  templateUrl: './image-viewer.component.html',
  styleUrl: './image-viewer.component.css',
})
export class ImageViewerComponent {
  @Input() images: string[] = [];
  @Input() width: string = '600px';
  @Input() height: string = '400px';
  @Input() showThumbnails: boolean = true;
  @Input() showFullscreen: boolean = true;
  @Input() loop: boolean = true;

  @Output() imageChanged = new EventEmitter<number>();

  zoomLevel = signal(1);
  rotation = signal(0);
  fullscreen = signal(false);
  slideshowActive = signal(false);
  theme = signal<'light' | 'dark'>('light');

  slideshowInterval: any;

  private _currentIndex = signal(0);

  @Input() set currentIndex(value: number) {
    if (typeof value === 'number') {
      this._currentIndex.set(value);
    }
  }

  get currentIndex() {
    return this._currentIndex();
  }

  constructor(private dialogRef: MatDialogRef<ImageViewerComponent>) {
    // Manually handle ESC key
    this.dialogRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.toggleFullscreen();
      }
    });
  }

  ngOnInit(){
    this.imageOnlyList()
    console.log("Image: ",this.images)
  }

  next() {
    if (this._currentIndex() < this.images.length - 1) {
      this._currentIndex.update(i => i + 1);
    } else if (this.loop) {
      this._currentIndex.set(0);
    }
    this.imageChanged.emit(this._currentIndex());
  }

  prev() {
    if (this._currentIndex() > 0) {
      this._currentIndex.update(i => i - 1);
    } else if (this.loop) {
      this._currentIndex.set(this.images.length - 1);
    }
    this.imageChanged.emit(this._currentIndex());
  }

  select(index: number) {
    this._currentIndex.set(index);
    this.imageChanged.emit(index);
  }

  toggleFullscreen() {
    this.dialogRef.close();
  }

  zoomIn() {
    this.zoomLevel.update(z => z + 0.1);
  }

  zoomOut() {
    if (this.zoomLevel() > 0.2) {
      this.zoomLevel.update(z => z - 0.1);
    }
  }

  rotateRight() {
    this.rotation.update(r => (r + 90) % 360);
  }

  rotateLeft() {
    this.rotation.update(r => (r - 90 + 360) % 360);
  }

  downloadImage() {
    const link = document.createElement('a');
    link.href = this.images[this._currentIndex()];
    link.download = `image-${this._currentIndex() + 1}.jpg`;
    link.click();
  }

  toggleSlideshow() {
    if (this.slideshowActive()) {
      this.stopSlideshow();
    } else {
      this.startSlideshow();
    }
  }

  startSlideshow() {
    this.slideshowActive.set(true);
    this.slideshowInterval = setInterval(() => {
      this.next();
    }, 2000);
  }

  stopSlideshow() {
    this.slideshowActive.set(false);
    clearInterval(this.slideshowInterval);
  }

  ngOnDestroy() {
    this.stopSlideshow();
  }

  toggleTheme() {
    this.theme.set(this.theme() === 'light' ? 'dark' : 'light');
  }

   // ✅ Extract file extension safely
  getFileExtension(path: string): string {
    const lastDotIndex = path.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === path.length - 1) {
      return ''; // no extension
    }
    return path.substring(lastDotIndex + 1).toLowerCase();
  }

  // ✅ Check if file is an image
  isImage(filePath: string): boolean {
    const extension = this.getFileExtension(filePath);
    return extension === 'png' || extension === 'jpg' || extension === 'jpeg'? true: false;
  }

  // ✅ Filter only images
  imageOnlyList(): string[] {
    return this.images = this.images.filter(img => this.isImage(img) == true);
  }


  getFileName(filePath: string): string {
    return filePath.split('/').pop() || 'file';
  }

}
